import type {
	CreateIngestionUploadsDto,
	CreateIngestionUploadsRes,
	EstimateIngestionDto,
	IngestionAnalyseEstimateRes,
	IngestionLibraryPageCountsRes,
	IngestionLibraryPageRes,
	IngestionProgressRes,
	IngestionPurgeRes,
	IngestionUploadListRes,
	IngestionUploadRes,
	IngestionUploadTargetRes,
	LibraryPageCountsDto,
	RenameIngestionBatchDto,
	StartIngestionDto,
	StartIngestionRes,
} from '@dealstack/shared/lib/generated';
import {
	createErrorInstance,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { uploadPutHeaders } from '@dealstack/shared/lib/dealstackAi/uploadPutHeaders';
import type { LibraryQuery } from './ingestion.types';

const UNPROCESSABLE_ENTITY = 422;

/** Sends the file's bytes to its presigned URL with every header the server signed. */
export const putUploadBody = async (
	upload: IngestionUploadTargetRes,
	file: Blob
): Promise<void> => {
	const response = await fetch(upload.uploadUrl, {
		method: 'PUT',
		headers: uploadPutHeaders(upload),
		body: file,
	});
	if (!response.ok) {
		throw new Error(`Upload failed with status ${response.status}`);
	}
};

/** Wakes the extraction pods and GPU readers; their cold start is most of the wait. */
export const warmUpIngestion = async (): Promise<void> => {
	return makeRequest<void>({
		endpoint: API_PATHS.DEALSTACK_AI_INGESTION_WARM_UP_POST,
		method: HTTP_METHOD.POST,
	});
};

export const getIngestionUploads = async (
	signal?: AbortSignal
): Promise<IngestionUploadListRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionUploadListRes>>(
		{
			endpoint: API_PATHS.DEALSTACK_AI_INGESTION_UPLOADS_GET,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

/** Presigned PDF uploads, after the library check ("use the library copy"). */
export const createIngestionUploads = async (
	args: CreateIngestionUploadsDto
): Promise<CreateIngestionUploadsRes | undefined> => {
	return makeRequest<ApiV1.Response<CreateIngestionUploadsRes>>({
		endpoint: API_PATHS.DEALSTACK_AI_INGESTION_UPLOADS_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

/** Has the server count an uploaded file's pages. */
export const completeIngestionUpload = async (
	fileId: string
): Promise<IngestionUploadRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionUploadRes>>({
		endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_UPLOADS_FILEID_COMPLETE_POST, {
			fileId,
		}),
		method: HTTP_METHOD.POST,
	}).then((response) => response.payload);
};

export const removeIngestionUpload = async (fileId: string): Promise<void> => {
	return makeRequest<void>({
		endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_UPLOADS_FILEID_DELETE, { fileId }),
		method: HTTP_METHOD.DELETE,
	});
};

/** The scope and time of a selection, before anything is started. */
export const estimateIngestion = async (
	args: EstimateIngestionDto
): Promise<IngestionAnalyseEstimateRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionAnalyseEstimateRes>>({
		endpoint: API_PATHS.DEALSTACK_AI_INGESTION_ESTIMATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

/**
 * Starts a batch. A start over the page cap answers 422 with the split body itself rather than the usual
 * `{ error }` envelope, and the wrapper keeps only that envelope's `error` — so this one call takes the raw
 * failure and rethrows the split body, leaving every other status to the shared wrapper.
 */
export const startIngestion = async (
	args: StartIngestionDto
): Promise<StartIngestionRes | undefined> => {
	const endpoint = API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_POST;
	try {
		return await makeRequest<ApiV1.Response<StartIngestionRes>>(
			{ endpoint, method: HTTP_METHOD.POST, body: JSON.stringify(args) },
			{ skipErrorHandler: true }
		).then((response) => response.payload);
	} catch (error) {
		if (error instanceof Response && error.status === UNPROCESSABLE_ENTITY) {
			throw await error.json();
		}
		throw await createErrorInstance(error, undefined, {
			endpoint,
			method: HTTP_METHOD.POST,
		});
	}
};

/** The deal's document library, filtered and paged, with the documents a batch would suggest. */
export const getIngestionLibrary = async (
	query: LibraryQuery,
	signal?: AbortSignal
): Promise<IngestionLibraryPageRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionLibraryPageRes>>(
		{
			endpoint: API_PATHS.DEALSTACK_AI_INGESTION_LIBRARY_GET,
			method: HTTP_METHOD.GET,
			queryString: query,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

/** Page counts for a library pick, so the footer can total them before anything starts. */
export const countIngestionLibraryPages = async (
	args: LibraryPageCountsDto
): Promise<IngestionLibraryPageCountsRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionLibraryPageCountsRes>>({
		endpoint: API_PATHS.DEALSTACK_AI_INGESTION_LIBRARY_PAGE_COUNTS_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

export const getIngestionProgress = async (
	batchId: string,
	signal?: AbortSignal
): Promise<IngestionProgressRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionProgressRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_BATCHID_PROGRESS_GET, {
				batchId,
			}),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const cancelIngestionBatch = async (batchId: string): Promise<void> => {
	return makeRequest<void>({
		endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_BATCHID_CANCEL_POST, {
			batchId,
		}),
		method: HTTP_METHOD.POST,
	});
};

export const renameIngestionBatch = async (
	batchId: string,
	args: RenameIngestionBatchDto
): Promise<void> => {
	return makeRequest<void>({
		endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_BATCHID_TITLE_PATCH, {
			batchId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(args),
	});
};

/** Deletes the batch's source files now, rather than waiting for the retention window. */
export const deleteIngestionFilesNow = async (
	batchId: string
): Promise<IngestionPurgeRes | undefined> => {
	return makeRequest<ApiV1.Response<IngestionPurgeRes>>({
		endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_BATCHID_PURGE_POST, {
			batchId,
		}),
		method: HTTP_METHOD.POST,
	}).then((response) => response.payload);
};

export const deleteIngestionBatch = async (batchId: string): Promise<void> => {
	return makeRequest<void>({
		endpoint: formatUnicorn(API_PATHS.DEALSTACK_AI_INGESTION_BATCHES_BATCHID_DELETE, {
			batchId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};
